/**
 * Demo data for the pursuit engines (?demo=1) — shaped like the real
 * /.netlify/functions/pursuits response { results, owners, config, after }.
 * NEVER real data. Mirrors the pursuit-tracker's config-driven model: each
 * "line" is one engine, company- or deal-sourced.
 */
export const PURSUIT_LINES = [
  { key: 'letterwinner', label: 'Letterwinner' },
  { key: 'rfp', label: 'RFP / Bids' },
  { key: 'conference', label: 'Conference' },
  { key: 'bands', label: 'Bands & Entertainment' },
  { key: 'world_cup', label: 'World Cup' },
  { key: 'corporate', label: 'Corporate' },
  { key: 'halls', label: 'Halls of Fame' },
  { key: 'government', label: 'Government' },
  { key: 'cheer', label: 'Cheer' },
]

const COMPANY_STATUS = ['Not Started', 'To Pursue', 'Outreach In Progress', 'Partnered']
const DEAL_STAGES = [
  { value: 'surfaced', label: 'Surfaced' },
  { value: 'qualified', label: 'Categorized & Qualified' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'engaged', label: 'Outreach/Engaged' },
  { value: 'proposal', label: 'Proposal/Quote' },
  { value: 'verbal', label: 'Verbal/Won' },
  { value: 'won', label: 'Closed Won' },
]
const VALUE_BANDS = ['Under $25K', '$25K-$75K', '$75K-$150K', '$150K-$400K', '$400K+']
// RFP is deal-sourced in the real portal (HubSpot pipeline 914387521), so the
// demo line is too — same shape, same property names, fictional values.
const DEAL_LINES = new Set(['bands', 'world_cup', 'rfp'])

const OWNERS = { o1: 'Marcus Bell', o2: 'Aisha Rahman', o3: 'Dana Whitfield' }

function companyConfig(line, label) {
  return {
    sourceType: 'company', label,
    statusProp: 'sr_pursuit_status', tierProp: 'sr_tier', valueProp: 'sr_value_band',
    nameProp: 'name', associationProp: 'sr_has_relationship',
    statusOptions: COMPANY_STATUS, tierOptions: ['Tier 1', 'Tier 2', 'Tier 3'], valueOptions: VALUE_BANDS,
    metrics: [], lines: PURSUIT_LINES,
  }
}
function dealConfig(line, label) {
  return {
    sourceType: 'deal', label,
    statusProp: 'dealstage', valueProp: 'amount', nameProp: 'dealname', originProp: 'sr_origin',
    statusOptions: DEAL_STAGES, metrics: ['sr_crew_size'], lines: PURSUIT_LINES,
  }
}

const CONF = ['SEC', 'Big Ten', 'ACC', 'Pac-12', 'Sun Belt', 'OUA']
const DIV = ['D-I', 'D-II', 'D-III', 'HS 6A', 'HS 5A']
const STATES = ['TX', 'CA', 'FL', 'GA', 'OH', 'NY', 'ON', 'QC']

function companyRows(line, n) {
  const rows = []
  for (let i = 0; i < n; i++) {
    rows.push({
      id: `${line}-${i}`,
      name: `${['Westlake', 'Riverside', 'Northgate', 'Summit', 'Cedar Valley', 'Harbor Point', 'Maple Grove', 'Sterling'][i % 8]} ${line === 'letterwinner' ? 'University' : 'Program'}`,
      sr_tier: ['Tier 1', 'Tier 2', 'Tier 3'][i % 3],
      sr_value_band: VALUE_BANDS[i % VALUE_BANDS.length],
      sr_pursuit_status: COMPANY_STATUS[i % COMPANY_STATUS.length],
      sr_conference: CONF[i % CONF.length],
      sr_division: DIV[i % DIV.length],
      state: STATES[i % STATES.length],
      sr_mascot: ['Falcons', 'Tigers', 'Eagles', 'Wolves'][i % 4],
      sr_has_relationship: i % 3 === 0 ? 'Yes' : i % 3 === 1 ? 'No' : 'Unknown',
      sr_est_annual_letterwinners: 40 + (i * 13) % 120,
      sr_est_living_letterwinners: 800 + (i * 137) % 4000,
      sr_assigned_rep: Object.values(OWNERS)[i % 3],
    })
  }
  return rows
}
/* ---- The RFP line (deal-sourced, mirrors the live mapping) ---------- */

const RFP_CATEGORIES = [
  'Awards & Recognition', 'Diplomas & Announcements', 'Graduation Regalia',
  'Class Rings & Jewelry', 'Uniforms & Apparel',
]
const RFP_STATUS = ['Open', 'Open', 'Open', 'Submitted', 'Awarded']
const RFP_ISSUERS = [
  'Guilford County Schools', 'City of Cedar Falls', 'State University System',
  'Harbor Point ISD', 'County of Westlake',
]

/**
 * Demo RFP deals, carrying the SAME keys the backend maps onto a live row —
 * closeDate / openDate / state / product / status / sourceUrl / brand / number
 * / issuer — plus the raw HubSpot property names beside them. Dates are spread
 * across the rolling window (some already closed) so the countdown, the
 * look-back/look-forward lens and the overdue styling are all exercised.
 */
function rfpRows(n, now = new Date()) {
  const day = 86400000
  const iso = (d) => new Date(d).toISOString().slice(0, 10)
  const rows = []
  for (let i = 0; i < n; i++) {
    // −20 … +100 days out, deterministic.
    const closeIn = ((i * 17) % 121) - 20
    const close = now.getTime() + closeIn * day
    const scan = close - (14 + (i * 5) % 40) * day
    const number = `RFP-${2026}-${String(1000 + i * 7).padStart(4, '0')}`
    rows.push({
      id: `rfp-${i}`,
      dealname: `${RFP_ISSUERS[i % RFP_ISSUERS.length]} — ${RFP_CATEGORIES[i % RFP_CATEGORIES.length]}`,
      // mapped names (what the Command Center reads)
      closeDate: iso(close),
      openDate: iso(scan),
      state: STATES[i % STATES.length],
      product: RFP_CATEGORIES[i % RFP_CATEGORIES.length],
      status: RFP_STATUS[i % RFP_STATUS.length],
      // Most live RFP deals are unassigned — the demo says so too.
      owner: i % 5 === 0 ? Object.values(OWNERS)[i % 3] : '',
      ownerId: '',
      sourceUrl: `https://example.com/solicitations/${number.toLowerCase()}`,
      brand: i % 3 === 0 ? 'POMP' : 'SR',
      number,
      issuer: RFP_ISSUERS[i % RFP_ISSUERS.length],
      // raw HubSpot property names, carried alongside
      closedate: iso(close),
      sr_rfp_scan_date: iso(scan),
      sr_rfp_state: STATES[i % STATES.length],
      sr_rfp_category: RFP_CATEGORIES[i % RFP_CATEGORIES.length],
      sr_rfp_status: RFP_STATUS[i % RFP_STATUS.length],
      sr_rfp_source_url: `https://example.com/solicitations/${number.toLowerCase()}`,
      sr_rfp_brand: i % 3 === 0 ? 'POMP' : 'SR',
      sr_rfp_number: number,
      sr_rfp_issuer: RFP_ISSUERS[i % RFP_ISSUERS.length],
      amount: null,
      hubspot_owner_id: '',
    })
  }
  return rows
}

function rfpConfig(label) {
  return {
    sourceType: 'deal', label, line: 'rfp',
    statusProp: 'status', nameProp: 'dealname', valueProp: 'amount',
    dateProp: 'closeDate', dateLabel: 'Close date', dateKind: 'close',
    openDateProp: 'openDate', openDateLabel: 'Surfaced', openDateIsProxy: true,
    productProp: 'product', productLabel: 'Product category',
    statusOptions: [], metrics: [], lines: PURSUIT_LINES,
  }
}

/* ---- The corporate line (EMPLOYEE RECOGNITION, mirrors the live mapping) --- */

const INDUSTRIES = [
  'Machinery', 'Financial Services', 'Hospital & Health Care', 'Transportation/Trucking/Railroad',
  'Food & Beverages', 'Utilities', 'Construction', 'Insurance',
]
const HQ = [
  ['Charlotte', 'NC'], ['Atlanta', 'GA'], ['Dallas', 'TX'], ['Columbus', 'OH'],
  ['Tampa', 'FL'], ['Nashville', 'TN'], ['Phoenix', 'AZ'], ['Denver', 'CO'],
]
const LEAD_STATUS = ['NEW', 'OPEN', 'IN_PROGRESS', 'CONNECTED']

/**
 * Demo CORPORATIONS, carrying the SAME keys the backend maps onto a live row —
 * employees / industry / hqCity / hqLocation / website / pursuitLevel / owner —
 * plus the raw HubSpot property names beside them.
 *
 * Deliberately shaped like the live data's GAPS, not an idealized set: most
 * records have no SR owner and no lead status (both are backfill work on the
 * data side), and some have no headcount at all. If the demo were fully
 * populated it would prove nothing about how the space handles the real thing.
 */
function corporateRows(n) {
  const rows = []
  for (let i = 0; i < n; i++) {
    const [city, state] = HQ[i % HQ.length]
    const name = `${['Meridian', 'Cardinal', 'Blue Ridge', 'Ironworks', 'Summit', 'Halcyon', 'Piedmont', 'Kestrel'][i % 8]} ${['Manufacturing', 'Financial', 'Health', 'Logistics', 'Foods', 'Energy', 'Builders', 'Assurance'][i % 8]}`
    const domain = name.toLowerCase().replace(/[^a-z]+/g, '') + '.example'
    // 1 in 6 has no headcount recorded — an honest unknown, not a zero.
    const employees = i % 6 === 5 ? null : 250 + ((i * 1373) % 48000)
    // Owner and lead status are unset on most records, as they are live.
    const ownerId = i % 7 === 0 ? `o${(i % 3) + 1}` : ''
    const lead = i % 4 === 0 ? LEAD_STATUS[i % LEAD_STATUS.length] : ''
    rows.push({
      id: `corporate-${i}`,
      name,
      // mapped names (what the Command Center reads)
      employees,
      industry: INDUSTRIES[i % INDUSTRIES.length],
      hqCity: city,
      state,
      country: 'United States',
      hqLocation: `${city}, ${state}`,
      website: `https://${domain}`,
      owner: ownerId ? OWNERS[ownerId] : '',
      ownerId,
      pursuitLevel: lead || null,
      sr_pursuit_status: COMPANY_STATUS[i % COMPANY_STATUS.length],
      sr_assigned_rep: '',
      // raw HubSpot property names, carried alongside
      numberofemployees: employees == null ? '' : String(employees),
      city,
      domain,
      hs_lead_status: lead,
      sr_segment: 'Corporation',
      hubspot_owner_id: ownerId,
    })
  }
  return rows
}

function corporateConfig(label) {
  return {
    sourceType: 'company', kind: 'employee-recognition', label, line: 'corporate',
    statusProp: 'sr_pursuit_status', nameProp: 'name', nameLabel: 'Company',
    employeesProp: 'employees', employeesLabel: 'Total employees',
    industryProp: 'industry', industryLabel: 'Industry',
    hqProp: 'hqLocation', hqLabel: 'HQ location',
    websiteProp: 'website', websiteLabel: 'Website',
    levelProp: 'pursuitLevel', levelLabel: 'Pursuit level',
    // Not a championship: nothing to count down to.
    dateProp: null, dateKind: '',
    statusOptions: COMPANY_STATUS, metrics: [], lines: PURSUIT_LINES,
  }
}

/* ---- Police / Fire — the COMMUNITY HERO program -------------------- */

const HERO_STATUSES = ['Nominated', 'Approved', 'In production', 'Presented']
const HERO_NAMES = [
  ['Sgt. Ramona Vale', 'Sergeant'], ['Capt. Elias Munro', 'Captain'],
  ['Lt. Deshawn Pryor', 'Lieutenant'], ['Officer Nell Kavanagh', 'Officer'],
  ['Chief Marta Okoye', 'Chief'],
]

/**
 * Demo POLICE / FIRE departments, carrying the SAME keys the backend maps onto a
 * live row — including the associated `hero` deal that IS the Community Hero
 * nomination.
 *
 * Deliberately shaped like the real gaps: MOST departments have no hero at all
 * (that is the whole worklist), one has a WITHDRAWN nomination so the reopened
 * slot is exercised, and owners are mostly unassigned. A fully-nominated demo
 * would prove nothing about how the space handles the real thing.
 */
function departmentRows(line, n) {
  const day = 86400000
  const iso = (d) => new Date(d).toISOString().slice(0, 10)
  const kindWord = line === 'fire' ? 'Fire Department' : 'Police Department'
  const rows = []
  for (let i = 0; i < n; i++) {
    const [city, state] = HQ[i % HQ.length]
    const ownerId = i % 5 === 0 ? `o${(i % 3) + 1}` : ''
    // 1 in 3 has a hero; one of those is withdrawn, so its slot reads OPEN.
    const hasHero = i % 3 === 0
    const withdrawn = i === 6
    const [heroName, rank] = HERO_NAMES[i % HERO_NAMES.length]
    const hero = hasHero
      ? {
        id: `${line}-hero-${i}`,
        dealname: `Community Hero — ${city} ${kindWord}`,
        heroName,
        rank,
        status: withdrawn ? 'Withdrawn' : HERO_STATUSES[i % HERO_STATUSES.length],
        stage: withdrawn ? 'Withdrawn' : HERO_STATUSES[i % HERO_STATUSES.length],
        owner: OWNERS[`o${(i % 3) + 1}`],
        ownerId: `o${(i % 3) + 1}`,
        yearsOfService: 18 + (i * 3) % 22,
        createdate: iso(Date.now() - (30 + i * 11) * day),
        closedate: '',
      }
      : null
    rows.push({
      id: `${line}-${i}`,
      name: `${city} ${kindWord}`,
      orgType: kindWord,
      sr_org_type: kindWord,
      city,
      state,
      location: `${city}, ${state}`,
      website: `https://${city.toLowerCase().replace(/\W+/g, '')}${line}.example`,
      owner: ownerId ? OWNERS[ownerId] : '',
      ownerId,
      hubspot_owner_id: ownerId,
      sr_pursuit_status: COMPANY_STATUS[i % COMPANY_STATUS.length],
      sr_assigned_rep: '',
      sr_segment: 'Government/Public Safety',
      // THE ASSOCIATED NOMINATION DEAL — null on most departments, which is the
      // honest and useful state.
      hero,
      heroCount: hero && !withdrawn ? 1 : 0,
    })
  }
  return rows
}

function departmentConfig(line, label) {
  return {
    sourceType: 'company', kind: 'community-hero', label, line,
    statusProp: 'sr_pursuit_status', nameProp: 'name',
    orgTypeProp: 'orgType', orgTypeLabel: 'Organization type',
    hqProp: 'location', hqLabel: 'Location',
    websiteProp: 'website', websiteLabel: 'Website',
    // A department has no event and no deadline: nothing counts down.
    dateProp: null, dateKind: '',
    statusOptions: COMPANY_STATUS, metrics: [], lines: PURSUIT_LINES,
  }
}

/* ---- Greek — PURSUIT TRACKING -------------------------------------- */

const GREEK_ORGS = [
  ['Alpha Sigma Phi National Headquarters', 'National Organization'],
  ['Kappa Delta Rho — Beta Chapter', 'Greek Chapter'],
  ['Delta Gamma — Eta Chapter', 'Greek Chapter'],
  ['Sigma Nu International Fraternity', 'National Organization'],
  ['Chi Omega — Psi Chapter', 'Greek Chapter'],
  ['Phi Beta Sigma — Alpha Chapter', 'Greek Chapter'],
]
const GREEK_PRODUCTS_DEMO = [
  'Badges & Insignia', 'Chapter Rings', 'Recognition Jewelry',
  'Awards & Plaques', 'Founders Day Commemoratives',
]

/**
 * Demo GREEK organizations with their associated PURSUIT deals — product,
 * owner, the date the pursuit opened and, on the won ones, a real won date.
 *
 * Mirrors the live gaps again: some organizations have no pursuit at all, some
 * are open, some are won, and one is lost (which has a close date HubSpot-side
 * but must NOT report a won date). Nationals and chapters are both present so
 * the nationals-above-chapters grouping is actually exercised.
 */
function greekRows(n) {
  const day = 86400000
  const iso = (d) => new Date(d).toISOString().slice(0, 10)
  const rows = []
  for (let i = 0; i < n; i++) {
    const [name, orgType] = GREEK_ORGS[i % GREEK_ORGS.length]
    const [city, state] = HQ[i % HQ.length]
    const ownerId = `o${(i % 3) + 1}`
    // 1 in 4 has no pursuit opened; of the rest, every 3rd is won and one lost.
    const hasPursuit = i % 4 !== 3
    const won = hasPursuit && i % 3 === 0
    const lost = hasPursuit && i === 5
    const opened = Date.now() - (40 + i * 19) * day
    const pursuitDeal = hasPursuit
      ? {
        id: `greek-deal-${i}`,
        dealname: GREEK_PRODUCTS_DEMO[i % GREEK_PRODUCTS_DEMO.length],
        product: GREEK_PRODUCTS_DEMO[i % GREEK_PRODUCTS_DEMO.length],
        status: won ? 'Closed Won' : lost ? 'Closed Lost' : 'Outreach In Progress',
        stage: won ? 'closedwon' : lost ? 'closedlost' : 'engaged',
        owner: OWNERS[ownerId],
        ownerId,
        won,
        lost,
        pursuitDate: iso(opened),
        createdate: iso(opened),
        // A close date exists on the lost deal too — the adapter must refuse to
        // report it as a WON date.
        closedate: won || lost ? iso(opened + 30 * day) : '',
      }
      : null
    rows.push({
      id: `greek-${i}`,
      name: `${name}${i >= GREEK_ORGS.length ? ` ${Math.floor(i / GREEK_ORGS.length) + 1}` : ''}`,
      orgType,
      sr_org_type: orgType,
      city,
      state,
      location: `${city}, ${state}`,
      website: `https://greek${i}.example`,
      owner: '',
      ownerId: '',
      hubspot_owner_id: '',
      sr_pursuit_status: COMPANY_STATUS[i % COMPANY_STATUS.length],
      sr_assigned_rep: '',
      sr_segment: 'Greek Life',
      pursuitDeal,
      pursuitCount: pursuitDeal ? 1 : 0,
    })
  }
  return rows
}

function greekConfig(label) {
  return {
    sourceType: 'company', kind: 'greek-pursuit', label, line: 'greek',
    statusProp: 'sr_pursuit_status', nameProp: 'name',
    orgTypeProp: 'orgType', orgTypeLabel: 'Organization type',
    hqProp: 'location', hqLabel: 'Location',
    websiteProp: 'website', websiteLabel: 'Website',
    productLabel: 'Product pursued',
    dateProp: null, dateKind: '',
    statusOptions: COMPANY_STATUS, metrics: [], lines: PURSUIT_LINES,
  }
}

/* ---- Cheer — THE GYM PARTNERSHIP MACHINE --------------------------- */

/**
 * THE DEMO CHEER TAM. Exported so the demo CHAMPIONSHIP universe
 * (`mockChampionships.js`) can crown these gyms' TEAMS — which is the only way
 * `?demo=1` proves the gym → team → championship rollup actually works rather
 * than just rendering an empty column.
 *
 * Deliberately shaped like the real thing: MOST gyms are targets (that is the
 * TAM job), one has a signed contract with no jewelry program yet (the state
 * that must never read as a partnership), one is a partner, one lost its
 * pursuit and is back in the TAM, and several have championship footprints
 * while having no relationship with us at all — which is exactly the list a rep
 * should be working from.
 */
export const DEMO_CHEER_GYMS = [
  { name: 'Lone Star Elite Cheer', state: 'TX', teams: ['Comets', 'Nova', 'Rebels'], stage: 'partner' },
  { name: 'Cardinal Cheer Company', state: 'OH', teams: ['Crimson', 'Ruby', 'Scarlet', 'Cadets'], stage: 'target' },
  { name: 'Bluegrass Athletics', state: 'GA', teams: ['Lady Jets', 'Vortex'], stage: 'contract' },
  { name: 'Harborview All Stars', state: 'FL', teams: ['Tidal', 'Reef'], stage: 'pursuit' },
  { name: 'Summit Spirit Athletics', state: 'CO', teams: ['Peak', 'Avalanche'], stage: 'lost' },
  { name: 'Gulf Coast Cheer Academy', state: 'TX', teams: ['Surge'], stage: 'pursuit-unowned' },
  { name: 'Piedmont Twisters', state: 'NC', teams: [], stage: 'target' },
  { name: 'Sierra Storm Allstars', state: 'CA', teams: ['Thunder', 'Lightning'], stage: 'partner' },
  { name: 'Great Lakes Spirit Elite', state: 'OH', teams: ['Mariners'], stage: 'target' },
  { name: 'Copperhead Cheer Athletics', state: 'AZ', teams: [], stage: 'target' },
  { name: 'Northgate Cheer Force', state: 'NY', teams: ['Sentinels'], stage: 'target' },
  { name: 'Magnolia Cheer Collective', state: 'GA', teams: [], stage: 'target' },
]

const CHEER_JEWELRY = {
  partner: 'Exclusive branded collection — LIVE',
  contract: 'Exclusive collection in design',
}

function cheerRows() {
  const day = 86400000
  const iso = (d) => new Date(d).toISOString().slice(0, 10)
  return DEMO_CHEER_GYMS.map((g, i) => {
    const opened = Date.now() - (60 + i * 23) * day
    const signed = opened + 45 * day
    const owned = g.stage !== 'pursuit-unowned'
    const hasDeal = g.stage !== 'target'
    const won = g.stage === 'partner' || g.stage === 'contract'
    const lost = g.stage === 'lost'
    const partnershipDeal = hasDeal
      ? {
        id: `cheer-deal-${i}`,
        dealname: `Cheer Partnership — ${g.name}`,
        program: i % 2 ? 'Championship rings' : 'Exclusive branded jewelry program',
        status: won ? 'Closed Won' : lost ? 'Closed Lost' : 'Outreach In Progress',
        stage: won ? 'closedwon' : lost ? 'closedlost' : 'engaged',
        owner: owned ? Object.values(OWNERS)[i % 3] : '',
        ownerId: owned ? `o${(i % 3) + 1}` : '',
        won,
        lost,
        pursuitDate: iso(opened),
        createdate: iso(opened),
        // A close date exists on the LOST deal too — nothing may report it as a
        // signature.
        closedate: won || lost ? iso(signed) : '',
      }
      : null
    return {
      id: `cheer-${i}`,
      name: g.name,
      orgType: 'Cheer Gym',
      sr_org_type: 'Cheer Gym',
      city: '',
      state: g.state,
      location: g.state,
      website: `https://${g.name.toLowerCase().replace(/\W+/g, '')}.example`,
      // Most gyms are unowned until somebody opens a pursuit — as they are live.
      owner: '',
      ownerId: '',
      hubspot_owner_id: '',
      sr_pursuit_status: hasDeal ? 'Outreach In Progress' : 'To Pursue',
      sr_assigned_rep: '',
      sr_segment: 'Cheer',
      // The gym's OWN team count — a different number from "teams with titles",
      // and the demo keeps them apart on purpose.
      teamCount: 8 + (i % 5),
      // THE EXCLUSIVE BRANDED JEWELRY PROGRAM — an attribute of the gym.
      jewelryStatus: CHEER_JEWELRY[g.stage] || '',
      sr_jewelry_program: CHEER_JEWELRY[g.stage] || '',
      partnershipDeal,
      partnershipCount: partnershipDeal ? 1 : 0,
    }
  })
}

function cheerConfig(label) {
  return {
    sourceType: 'company', kind: 'cheer-partnership', label, line: 'cheer',
    statusProp: 'sr_pursuit_status', nameProp: 'name', nameLabel: 'Gym',
    orgTypeProp: 'orgType', orgTypeLabel: 'Organization type',
    hqProp: 'location', hqLabel: 'Location',
    websiteProp: 'website', websiteLabel: 'Website',
    productLabel: 'Partnership program',
    // A gym has no event and no deadline — its TEAMS' championships do, and
    // those live on the championships plane.
    dateProp: null, dateKind: '',
    statusOptions: COMPANY_STATUS, metrics: [], lines: PURSUIT_LINES,
  }
}

function dealRows(line, n) {
  const rows = []
  for (let i = 0; i < n; i++) {
    rows.push({
      id: `${line}-${i}`,
      dealname: line === 'world_cup' ? `World Cup Activation ${2026 + (i % 2)}` : `Touring Acts: ${['Nova', 'Skyline', 'Echoes', 'Marigold'][i % 4]} Tour`,
      amount: 250000 + (i * 175000) % 5000000,
      dealstage: DEAL_STAGES[i % DEAL_STAGES.length].value,
      sr_origin: ['Inbound', 'Referral', 'Discovery', 'RFP'][i % 4],
      sr_crew_size: 8 + (i * 5) % 60,
      hubspot_owner_id: `o${(i % 3) + 1}`,
    })
  }
  return rows
}

export function demoPursuits(line) {
  const meta = PURSUIT_LINES.find((l) => l.key === line) || PURSUIT_LINES[0]
  if (line === 'rfp') {
    return { results: rfpRows(24), owners: OWNERS, config: rfpConfig(meta.label), after: null }
  }
  if (line === 'corporate') {
    return {
      results: corporateRows(24),
      owners: OWNERS,
      config: corporateConfig('Corporate — Employee Recognition'),
      after: null,
    }
  }
  if (line === 'police' || line === 'fire') {
    return {
      results: departmentRows(line, 18),
      owners: OWNERS,
      config: departmentConfig(line, line === 'fire' ? 'Fire — Community Hero' : 'Police — Community Hero'),
      after: null,
    }
  }
  if (line === 'cheer') {
    return {
      results: cheerRows(),
      owners: OWNERS,
      config: cheerConfig('Cheer — Gym Partnerships'),
      after: null,
    }
  }
  if (line === 'greek') {
    return {
      results: greekRows(18),
      owners: OWNERS,
      config: greekConfig('Greek — Pursuit Tracking'),
      after: null,
    }
  }
  if (DEAL_LINES.has(line)) {
    return { results: dealRows(line, 14), owners: OWNERS, config: dealConfig(line, meta.label), after: null }
  }
  return { results: companyRows(line, 18), owners: OWNERS, config: companyConfig(line, meta.label), after: null }
}
